import { collection, doc, getDoc, getDocs, onSnapshot, query, where, orderBy, limit, startAfter, Timestamp, getCountFromServer, deleteDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getFirestoreInstance } from './config';

// Export Firestore using lazy initialization
// Instead of using a Proxy (which Firebase functions don't work well with),
// we use a getter function that returns the actual instance when accessed
// This ensures Firestore is initialized only when actually used, preventing chunking issues

let _firestoreInstance: ReturnType<typeof getFirestoreInstance> | null = null;

function getFirestoreInstanceLazy(): ReturnType<typeof getFirestoreInstance> {
    if (!_firestoreInstance) {
        _firestoreInstance = getFirestoreInstance();
    }
    return _firestoreInstance;
}

const handler: ProxyHandler<ReturnType<typeof getFirestoreInstance>> = {
    get(_target, prop, receiver) {
        const instance = getFirestoreInstanceLazy();
        const value = Reflect.get(instance as unknown as Record<PropertyKey, unknown>, prop, receiver);
        return typeof value === 'function' ? value.bind(instance) : value;
    },
    set(_target, prop, value) {
        const instance = getFirestoreInstanceLazy();
        (instance as unknown as Record<PropertyKey, unknown>)[prop] = value;
        return true;
    },
    has(_target, prop) {
        const instance = getFirestoreInstanceLazy();
        return prop in (instance as unknown as Record<PropertyKey, unknown>);
    },
    ownKeys() {
        const instance = getFirestoreInstanceLazy();
        return Reflect.ownKeys(instance as unknown as Record<PropertyKey, unknown>);
    },
    getOwnPropertyDescriptor(_target, prop) {
        const instance = getFirestoreInstanceLazy();
        return Object.getOwnPropertyDescriptor(instance as unknown as Record<PropertyKey, unknown>, prop);
    },
    getPrototypeOf() {
        const instance = getFirestoreInstanceLazy();
        return Object.getPrototypeOf(instance);
    }
};

export const firestore = new Proxy({} as ReturnType<typeof getFirestoreInstance>, handler) as ReturnType<typeof getFirestoreInstance>;

// Export only the functions we use
export {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  getCountFromServer,
  deleteDoc,
  updateDoc,
  setDoc,
};

// Type exports
export type { Query, CollectionReference, WhereFilterOp } from 'firebase/firestore'; 